import { useEffect, useMemo, useState } from 'react';

import { SURVEY_TYPE_NPS, SURVEY_TYPE_RADIO_LIST } from '~/constants/survey';
import { Answer, SubmitProps, Survey } from '~/types/survey/types';
import { fetchJson } from '~/lib/fetch';

type UseSurvey = {
  isProcessing: boolean;
  surveys: Survey[];
  hasNPS: boolean;
  NPSSurvey?: Survey;
  radioListSurvey?: Survey;
  getAll: () => Promise<void>;
  dismiss: (id: number) => Promise<void>;
  submit: (data: SubmitProps) => Promise<void>;
};

export default function useSurvey(): UseSurvey {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const getAll = async () => {
    setIsProcessing(true);

    try {
      const { surveys } = await fetchJson(`/api/surveys/`, {
        method: 'GET',
        include: 'credentials',
      });

      if (surveys.length) {
        setSurveys(surveys);
      }
    } catch (error: any) {
      console.error('Error fetching surveys:', error);
    }

    setIsProcessing(false);
  };

  const dismiss = async (id: number) => {
    setIsProcessing(true);

    try {
      await fetchJson(`/api/surveys/${id}/dismiss/`, {
        method: 'POST',
        include: 'credentials',
      });
    } catch (error: any) {
      console.error('Error saving dismiss data', error);
    }

    setIsProcessing(false);
  };

  const submit = async ({
    id,
    questionId,
    choiceId,
    followUpQuestionId,
    text,
  }: SubmitProps) => {
    setIsProcessing(true);

    // Build answer array
    const answers: Answer[] = [
      {
        question: questionId,
        choice: choiceId,
      },
    ];

    if (followUpQuestionId && text) {
      answers.push({
        question: followUpQuestionId,
        text,
      });
    }

    try {
      await fetchJson(`/api/surveys/${id}/responses/`, {
        method: 'POST',
        include: 'credentials',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers }),
      });
    } catch (error: any) {
      console.error('Error submitting data', error);
    }

    setIsProcessing(false);
  };

  const NPSSurvey = useMemo(
    () => surveys.find((survey) => survey.slug === SURVEY_TYPE_NPS),
    [surveys],
  );

  const radioListSurvey = useMemo(
    () => surveys.find((survey) => survey.slug === SURVEY_TYPE_RADIO_LIST),
    [surveys],
  );

  const hasNPS = useMemo(() => !!NPSSurvey, [NPSSurvey]);

  useEffect(() => {
    getAll();
  }, []);

  return {
    isProcessing,
    surveys,
    hasNPS,
    NPSSurvey,
    radioListSurvey,
    getAll,
    dismiss,
    submit,
  };
}
